import React, { useEffect, useRef, useState } from 'react';
import GoogleMapsLoader from './GoogleMapsLoader';

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  onSelect: (address: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    formatted: string;
    raw_place?: google.maps.places.PlaceResult;
  }) => void;
  placeholder?: string;
  className?: string;
}

function AddressInput({
  value,
  onChange,
  onSelect,
  placeholder = "Enter an address",
  className = "",
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [lastSelectedPlace, setLastSelectedPlace] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Function to initialize the autocomplete with debug logging
  const initializeAutocomplete = () => {
    if (!inputRef.current) {
      console.error('Google Maps Autocomplete: Input ref not ready');
      return;
    }

    if (!window.google?.maps?.places) {
      console.error('Google Maps Autocomplete: Google Maps Places API not loaded');
      return;
    }

    try {
      console.log('Google Maps Autocomplete: Initializing autocomplete instance');

      // Clear any previous instances
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      
      const options: google.maps.places.AutocompleteOptions = {
        componentRestrictions: { country: "ca" },
        fields: [
          "address_components",
          "formatted_address",
          "geometry",
          "place_id"
        ],
        types: ["address"]
      };

      // Create new instance
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        options
      );

      console.log('Google Maps Autocomplete: Instance created with options:', options);

      // Add the place_changed listener
      if (autocompleteRef.current) {
        autocompleteRef.current.addListener("place_changed", handlePlaceSelect);
      }
      
      setInitialized(true);
      console.log('Google Maps Autocomplete: Listener added successfully');
    } catch (error) {
      console.error('Google Maps Autocomplete: Error initializing:', error);
    }
  };

  // Process a selected place with all address components
  const processPlace = (place: google.maps.places.PlaceResult) => {
    if (!place?.address_components) {
      console.error('Google Maps Autocomplete: No address components in place:', place);
      return;
    }

    // Extract all address components
    const components: { [key: string]: string } = {};
    place.address_components.forEach(component => {
      component.types.forEach(type => {
        components[type] = component.long_name;
        components[type + '_short'] = component.short_name;
      });
    });

    console.log('Google Maps Autocomplete: Extracted components:', components);

    // Build address parts
    const streetNumber = components['street_number'] || '';
    const route = components['route'] || '';
    const city = components['locality'] || components['postal_town'] || '';
    const province = components['administrative_area_level_1_short'] || components['administrative_area_level_1'] || '';
    const postalCode = components['postal_code'] || '';

    // Log each component as we extract it
    console.log('Google Maps Autocomplete: Extracted Street Number:', streetNumber);
    console.log('Google Maps Autocomplete: Extracted Route:', route);
    console.log('Google Maps Autocomplete: Extracted City:', city);
    console.log('Google Maps Autocomplete: Extracted Province:', province);
    console.log('Google Maps Autocomplete: Extracted Postal Code:', postalCode);

    // Build the street address
    const street = `${streetNumber} ${route}`.trim();
    
    // Build formatted address ensuring postal code is included
    const formattedParts = [
      street,
      city,
      province,
      postalCode,
      'Canada'
    ].filter(Boolean);

    const formattedAddress = formattedParts.join(', ');

    // Store the last selected place
    setLastSelectedPlace(formattedAddress);

    const addressData = {
      street,
      city,
      province,
      postalCode: postalCode || '',
      country: 'Canada',
      formatted: formattedAddress,
      raw_place: place
    };

    console.log('Google Maps Autocomplete: Final address data:', addressData);
    
    // Update the input value with the formatted address
    if (inputRef.current) {
      inputRef.current.value = formattedAddress;
    }
    
    onChange(formattedAddress);
    onSelect(addressData);
  };

  // Handle place selection
  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) {
      console.error('Google Maps Autocomplete: No autocomplete instance available');
      return;
    }
    
    const place = autocompleteRef.current.getPlace();
    console.log('Google Maps Autocomplete: Selected place:', place);

    if (!place?.place_id) {
      console.error('Google Maps Autocomplete: No place_id in selected place');
      return;
    }

    // If this is the first selection or a different place
    if (lastSelectedPlace !== (place.formatted_address || '')) {
      // Process the place immediately
      processPlace(place);
      
      // Then set a small timeout to fetch and process the place again
      // This ensures we get all components, including postal code
      setTimeout(() => {
        const service = new google.maps.places.AutocompleteService();
        const placesService = new google.maps.places.PlacesService(document.createElement('div'));
        
        service.getPlacePredictions(
          { input: place.formatted_address || '', componentRestrictions: { country: 'ca' } },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions?.[0]) {
              placesService.getDetails(
                { placeId: predictions[0].place_id, fields: ['address_components', 'formatted_address', 'geometry'] },
                (detailedPlace, detailStatus) => {
                  if (detailStatus === google.maps.places.PlacesServiceStatus.OK && detailedPlace) {
                    processPlace(detailedPlace);
                  }
                }
              );
            }
          }
        );
      }, 100);
    }
  };

  // Initialize when component mounts and Google Maps API is loaded
  useEffect(() => {
    const checkAndInitialize = () => {
      if (window.google?.maps?.places) {
        initializeAutocomplete();
      } else {
        console.log('Google Maps Autocomplete: Waiting for Google Maps API to load...');
        // Try again in 500ms
        setTimeout(checkAndInitialize, 500);
      }
    };

    checkAndInitialize();

    // Cleanup function
    return () => {
      if (autocompleteRef.current) {
        console.log('Google Maps Autocomplete: Cleaning up listeners');
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []); // Only run once on mount

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // If the user has cleared the input, reset lastSelectedPlace
    if (!newValue) {
      setLastSelectedPlace(null);
    }
    
    // Reinitialize autocomplete if it's not working
    if (newValue && !initialized) {
      console.log('Google Maps Autocomplete: Reinitializing due to user input but no initialization');
      initializeAutocomplete();
    }
  };

  // Handle click on the input field to ensure autocomplete is ready
  const handleInputClick = () => {
    if (!initialized) {
      console.log('Google Maps Autocomplete: Initializing on input click');
      initializeAutocomplete();
    }
  };

  // Handle focus on the input field
  const handleFocus = () => {
    if (!initialized) {
      console.log('Google Maps Autocomplete: Initializing on input focus');
      initializeAutocomplete();
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onClick={handleInputClick}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
        <div className="text-gray-400" title={initialized ? "Google Maps Autocomplete Ready" : "Google Maps Not Loaded"}>
          {initialized ? '✓' : '⚠️'}
        </div>
      </div>
    </div>
  );
}

export default function AddressAutocomplete(props: AddressAutocompleteProps) {
  // Read the API key from environment variables or use a default (for development only)
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "AIzaSyCGC4-0ZxPH8WLgxMYBhVmyscFSxk0Yhx8";
  
  return (
    <GoogleMapsLoader apiKey={apiKey}>
      <AddressInput {...props} />
    </GoogleMapsLoader>
  );
} 